import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MAX_LAYOUT_NAME_LENGTH, validateLayoutName, type HomeLayoutPrefs } from '@/lib/homeLayouts'

/** Asks for the name of a new Home layout before it is saved. */
export function NameLayoutDialog({
  open,
  onOpenChange,
  prefs,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  prefs: HomeLayoutPrefs
  onSave: (name: string) => void
}) {
  const [name, setName] = useState('')
  const [touched, setTouched] = useState(false)
  const error = validateLayoutName(prefs, name)

  const close = (next: boolean) => {
    if (!next) {
      setName('')
      setTouched(false)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className='max-w-sm'>
        <form
          onSubmit={e => {
            e.preventDefault()
            setTouched(true)
            if (error) return
            onSave(name.trim())
            close(false)
          }}
          className='grid gap-4'
        >
          <DialogHeader>
            <DialogTitle>Name this layout</DialogTitle>
            <DialogDescription>For example "Cleaning" or "Inventory".</DialogDescription>
          </DialogHeader>
          <div className='grid gap-1.5'>
            <Input
              autoFocus
              value={name}
              maxLength={MAX_LAYOUT_NAME_LENGTH}
              onChange={e => setName(e.target.value)}
              aria-label='Layout name'
              aria-invalid={touched && !!error}
            />
            {touched && error && <p className='text-xs text-red-600'>{error}</p>}
          </div>
          <DialogFooter className='gap-2'>
            <Button type='button' variant='outline' size='sm' onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type='submit' size='sm'>
              Save layout
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
